import { AnalyticsInstance } from 'analytics'
import { ConciergeAppointmentQuestionFlow } from '~types/concierge-enum'
import { Vehicle } from '~concierge/types/AppointmentTypes'

interface EventData {
  alfred_event_category?: string
  alfred_event_label?: string
  alfred_value?: number
  alfred_appointment_type?: string
  alfred_form_type?: 'form' | 'max' | 'direct'
  [key: string]: any
}

function getGTMContainer(): AnalyticsInstance | null {
  return (globalThis as any).gtmContainer || null
}

function shouldLogAnalytics(): boolean {
  if ((globalThis as any).__ANALYTICS_DEBUG__) {
    return document.querySelector('meta[name="log-analytics"]')?.getAttribute('content') === 'true'
  }
  return false
}

export async function trackEvent(eventName: string, eventData: EventData = {}): Promise<void> {
  const gtmContainer = getGTMContainer()
  if (!eventName.startsWith('alfred_')) {
    eventName = 'alfred_' + eventName
  }

  broadcastAnalyticsEvent(eventName, eventData)

  if (!gtmContainer) {
    if (shouldLogAnalytics()) {
      console.warn('GTM container not available for event tracking')
    }
    return
  }
  try {
    // Object.values(containers).forEach(async (analytics: AnalyticsInstance) => {
    if (shouldLogAnalytics()) {
      console.info('Tracking event:', eventName, eventData)
    }
    gtmContainer.ready((payload: any) => {
      console.log('all plugins have loaded or were skipped', payload)
    })
    if (gtmContainer && typeof gtmContainer.track === 'function') {
      // Looks like using gtmContainer.track duplicates events as many times as there are GTM codes on the page
      // const result = await gtmContainer.track(eventName, eventData)
      //
      // Using dataLayer.push instead to avoid duplicate events
      const result = (window as any).dataLayer.push({ event: eventName, ...eventData })

      if (shouldLogAnalytics()) {
        console.info('GTM event tracked successfully', result)
      }
    } else {
      if (shouldLogAnalytics()) {
        console.warn('Analytics instance or track method not available')
      }
    }
    // })
  } catch (error) {
    if (shouldLogAnalytics()) {
      console.error('Error tracking GTM event:', error)
    }
  }
}

export function trackUserSelection(
  appointmentType: ConciergeAppointmentQuestionFlow | 'unknown',
  form_type: 'form' | 'max' | 'direct' = 'form'
): void {
  const formFlow = form_type === 'form' ? getFormFlow(appointmentType) : 'none'

  trackEvent('alfred_user_has_made_a_selection', {
    alfred_event_category: 'user_interaction',
    alfred_event_label: 'appointment_selected',
    alfred_form_flow: formFlow,
    alfred_appointment_type: getAppointmentType(appointmentType),
    alfred_form_type: form_type
  })
}

export function trackUserInteractionStart(
  appointmentType: ConciergeAppointmentQuestionFlow | 'unknown',
  form_type: 'form' | 'max' | 'direct' = 'form'
): void {
  const formFlow = form_type === 'form' ? getFormFlow(appointmentType) : 'n/a'

  trackEvent('alfred_user_interaction_start', {
    alfred_event_category: 'user_interaction',
    alfred_event_label: 'appointment_selection',
    alfred_form_flow: formFlow,
    alfred_appointment_type: getAppointmentType(appointmentType),
    alfred_form_type: form_type
  })
}

export function trackFormSubmission(
  appointmentType: ConciergeAppointmentQuestionFlow | 'unknown',
  form_type: 'form' | 'max' | 'direct' = 'form'
): void {
  const formFlow = form_type === 'form' ? getFormFlow(appointmentType) : 'n/a'
  trackEvent('alfred_form_submit', {
    alfred_event_category: 'form',
    alfred_event_label: 'appointment_booking',
    alfred_form_flow: formFlow,
    alfred_appointment_type: getAppointmentType(appointmentType),
    alfred_form_type: form_type
  })
}

export function trackFormError(form_type: 'form' | 'max' | 'direct' = 'form'): void {
  trackEvent('alfred_form_validation_error', {
    alfred_event_category: 'form',
    alfred_event_label: 'appointment_booking',
    alfred_form_type: form_type
  })
}

export function trackFormSuccess(
  appointmentType: ConciergeAppointmentQuestionFlow | 'unknown',
  form_type: 'form' | 'max' | 'direct' = 'form',
  appointmentId?: string | undefined
): void {
  const id = appointmentId === undefined ? '' : appointmentId

  const formFlow = form_type === 'form' ? getFormFlow(appointmentType) : 'n/a'
  trackEvent('alfred_form_success', {
    alfred_event_category: 'conversion',
    alfred_event_label: 'appointment_booked',
    alfred_form_type: form_type,
    alfred_form_flow: formFlow,
    alfred_appointment_type: getAppointmentType(appointmentType),
    alfred_appointment_id: id
  })
}

export function trackConversion(
  appointmentType: ConciergeAppointmentQuestionFlow | 'unknown',
  form_type: 'form' | 'max' | 'direct' = 'form',
  value?: number
): void {
  const formFlow = form_type === 'form' ? getFormFlow(appointmentType) : 'n/a'
  trackEvent('alfred_conversion', {
    alfred_event_category: 'conversion',
    alfred_event_label: 'appointment_conversion',
    alfred_form_flow: formFlow,
    alfred_appointment_type: getAppointmentType(appointmentType),
    alfred_value: value
  })
}

export function trackTestDriveConversion(
  vehicle: Vehicle | undefined,
  vehiclesCount: number
): void {
  trackEvent('alfred_test_drive_booked', {
    alfred_event_category: 'conversion',
    alfred_event_label: 'test_drive',
    alfred_vehicles_count: vehiclesCount,
    alfred_vehicle_brand: vehicle?.brand || '',
    alfred_vehicle_model: vehicle?.model || '',
    alfred_vehicle_year: vehicle?.year || ''
  })
}

export function trackAdviserAppointmentConversion(): void {
  trackEvent('alfred_adviser_appointment_booked', {
    alfred_event_category: 'conversion',
    alfred_event_label: 'adviser_appointment'
  })
}

export function broadcastAnalyticsEvent(eventName: string, eventData: EventData = {}): void {
  window.parent.postMessage(
    {
      source: 'alfred',
      type: eventName,
      data: eventData
    },
    '*'
  )
}

function getAppointmentType(appointmentType: ConciergeAppointmentQuestionFlow | 'unknown'): string {
  switch (appointmentType) {
    case ConciergeAppointmentQuestionFlow.TestDriveAppointment:
      return 'test_drive'
    case ConciergeAppointmentQuestionFlow.AdviserSelectionAppointment:
      return 'adviser_appointment'
    case ConciergeAppointmentQuestionFlow.FreeAppointment:
      return 'adviser_appointment'
    default:
      return 'unknown'
  }
}
function getFormFlow(appointmentType: ConciergeAppointmentQuestionFlow | 'unknown'): string {
  switch (appointmentType) {
    case ConciergeAppointmentQuestionFlow.TestDriveAppointment:
      return 'test_drive'
    case ConciergeAppointmentQuestionFlow.AdviserSelectionAppointment:
      return 'adviser_selection'
    case ConciergeAppointmentQuestionFlow.FreeAppointment:
      return 'free_appointment'
    default:
      return 'unknown'
  }
}
